import {useEffect, useRef, useState} from "react";
import {Heading} from "./Heading";
import {Paragraph} from "./Paragraph";
import {useStateContext} from "../contexts/ContextProvider";

const BOOK_BASE_PATH = `${process.env.PUBLIC_URL || ""}/data/got1`;
const componentByType = {
    heading: Heading,
    paragraph: Paragraph
};

export const Book = () => {
    const {bookmarkJumpRequest} = useStateContext();
    const [bookMark, setBookMark] = useState(null);
    const [manifest, setManifest] = useState(null);
    const [chapterContent, setChapterContent] = useState([]);
    const [activeRange, setActiveRange] = useState({start: 0, end: 0});
    const [isLoadingChapter, setIsLoadingChapter] = useState(true);
    const [currentChapter, setCurrentChapter] = useState(0);
    const [pendingBookmarkScroll, setPendingBookmarkScroll] = useState(false);
    const currentChapterRef = useRef(0);

    const chapterTitles = manifest ? manifest.chapters.map(({title}, index) => title || `Chapter ${index + 1}`) : [];
    const chapterCount = manifest?.chapters?.length || 0;

    useEffect(() => {
        let ignore = false;

        const loadManifest = async () => {
            const res = await fetch(`${BOOK_BASE_PATH}/manifest.json`);
            const loadedManifest = await res.json();

            if (ignore) {
                return;
            }

            setManifest(loadedManifest);

            const localStorageIndex = localStorage.getItem("got1BookMarkIndex");
            const localStorageChapter = localStorage.getItem("got1ChapterIndex");

            if (localStorageChapter !== null) {
                const parsedChapter = Number(localStorageChapter);
                if (!Number.isNaN(parsedChapter)) {
                    setCurrentChapter(Math.max(0, Math.min(parsedChapter, loadedManifest.chapters.length - 1)));
                    return;
                }
            }

            if (localStorageIndex) {
                const parsedBookIndex = Number(String(localStorageIndex).split("-")[0]);
                if (!Number.isNaN(parsedBookIndex)) {
                    const chapterIndexFromBookmark = loadedManifest.chapters.findIndex(({startIndex, endIndex}) => {
                        return parsedBookIndex >= startIndex && parsedBookIndex <= endIndex;
                    });

                    if (chapterIndexFromBookmark !== -1) {
                        setCurrentChapter(chapterIndexFromBookmark);
                    }
                }
            }
        };

        loadManifest();

        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        const localStorageIndex = localStorage.getItem("got1BookMarkIndex");
        if (localStorageIndex) {
            setBookMark(localStorageIndex);
        }
    }, []);

    useEffect(() => {
        if (!manifest) {
            return;
        }

        if (currentChapter >= manifest.chapters.length) {
            setCurrentChapter(0);
            return;
        }

        let ignore = false;
        setIsLoadingChapter(true);

        const chapterMeta = manifest.chapters[currentChapter];
        if (!chapterMeta) {
            setChapterContent([]);
            setIsLoadingChapter(false);
            return;
        }

        const loadChapter = async () => {
            const res = await fetch(`${BOOK_BASE_PATH}/chapters/${chapterMeta.file}`);
            const chapterData = await res.json();

            if (ignore) {
                return;
            }

            setChapterContent(chapterData.items || []);
            setActiveRange({
                start: chapterData.startIndex || 0,
                end: chapterData.endIndex || 0
            });
            setIsLoadingChapter(false);
        };

        loadChapter();

        return () => {
            ignore = true;
        };
    }, [currentChapter, manifest]);

    useEffect(() => {
        if (!manifest) {
            return;
        }
        localStorage.setItem("got1ChapterIndex", String(currentChapter));
    }, [currentChapter, manifest]);

    useEffect(() => {
        currentChapterRef.current = currentChapter;
    }, [currentChapter]);

    useEffect(() => {
        if (!bookmarkJumpRequest) {
            return;
        }

        const localStorageIndex = localStorage.getItem("got1BookMarkIndex");
        const localStorageBookmarkChapter = localStorage.getItem("got1BookMarkChapter");

        if (!localStorageIndex) {
            return;
        }

        setBookMark(localStorageIndex);

        const scrollToBookmark = () => {
            const target = document.getElementById("bookmarked");
            if (target) {
                target.scrollIntoView({behavior: "smooth", block: "center"});
            }
        };

        if (localStorageBookmarkChapter === null) {
            scrollToBookmark();
            return;
        }

        const parsedChapter = Number(localStorageBookmarkChapter);
        if (Number.isNaN(parsedChapter)) {
            scrollToBookmark();
            return;
        }

        if (parsedChapter !== currentChapterRef.current) {
            setPendingBookmarkScroll(true);
            setCurrentChapter(parsedChapter);
            return;
        }

        scrollToBookmark();
    }, [bookmarkJumpRequest]);

    useEffect(() => {
        if (!pendingBookmarkScroll || isLoadingChapter) {
            return;
        }

        const frame = requestAnimationFrame(() => {
            const target = document.getElementById("bookmarked");
            if (target) {
                target.scrollIntoView({behavior: "smooth", block: "center"});
            }
            setPendingBookmarkScroll(false);
        });

        return () => cancelAnimationFrame(frame);
    }, [pendingBookmarkScroll, isLoadingChapter, chapterContent]);

    const renderChapterControls = () => (
        <div className="py-2 my-4 flex gap-2 items-center">
            <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={currentChapter === 0}
                onClick={() => setCurrentChapter(prev => Math.max(0, prev - 1))}
            >
                Prev
            </button>
            <select
                className="px-2 py-1 border rounded max-w-full"
                disabled={!manifest}
                value={currentChapter}
                onChange={(e) => setCurrentChapter(Number(e.target.value))}
            >
                {chapterTitles.map((title, chapterIndex) => (
                    <option key={chapterIndex} value={chapterIndex}>
                        {chapterIndex + 1}. {title}
                    </option>
                ))}
            </select>
            <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={currentChapter >= chapterCount - 1}
                onClick={() => setCurrentChapter(prev => Math.min(chapterCount - 1, prev + 1))}
            >
                Next
            </button>
        </div>
    );

    return (
        <div className="p-8">
            {renderChapterControls()}

            {isLoadingChapter && <p>Loading chapter...</p>}

            {!isLoadingChapter && chapterContent.map((elem, localIndex) => {
                const Component = componentByType[elem.type];
                const originalIndex = activeRange.start + localIndex;

                if (!Component) {
                    return null;
                }

                return <Component
                    key={originalIndex}
                    content={elem.content}
                    index={originalIndex}
                    bookMark={bookMark}
                    setBookMark={setBookMark}
                    currentChapter={currentChapter}
                />;
            })}

            {!isLoadingChapter && renderChapterControls()}
        </div>
    );
};
